
import emotiiWebSocket from "./emotiiWebSocket";

class WebSocketHandler {
  static CHANNEL_NAMES = [
    "MessagesChannel",
    "CallChannel",
    "ConferenceCallChannel",
    "NotificationsChannel",
    "CallsChannel",
    "MeetingChannel",
    "ConversationsChannel",
  ];

  static instance = null;

  constructor() {
    if (WebSocketHandler.instance) {
      return WebSocketHandler.instance;
    }

    this.ws = new emotiiWebSocket();
    this.observers = [];

    WebSocketHandler.instance = this;
  }

  connect(disconnectCallback) {
    this.ws.connect(disconnectCallback);
    this.#subscribeToChannels();
  }

  disconnect() {
    this.ws.disconnect();
  }

  isConnected() {
    return this.ws.isConnected();
  }

  // WebSocketObserver
  addObserver(observer) {
    this.observers.push(observer);
  }

  // WebSocketObserver
  removeObserver(observer) {
    this.observers = this.observers.filter((obs) => obs !== observer);
  }

  sendCloseCallMessage(callUuid) {
    const dataObject = {
      call_uuid: callUuid,
      update: "close",
    };
    this.#sendCallMessage(dataObject);
  }


  sendCancelSpeakMessage(callUuid, channelName = "CallChannel") {
    const dataObject = {
      call_uuid: callUuid,
      update: "cancel_speak",
    };
    this.#sendChannelMessage(channelName, dataObject);
  }

  sendRequestSpeakMessage(callUuid, payload = {}, channelName = "CallChannel") {
    const dataObject = {
      call_uuid: callUuid,
      update: "request_speak",
      ...payload,
    };
    this.#sendChannelMessage(channelName, dataObject);
  }

  sendReorderSpeakersMessage(callUuid, userIds, channelName = "CallChannel") {
    const dataObject = {
      call_uuid: callUuid,
      update: "reorder_speakers",
      user_ids: userIds,
    };
    this.#sendChannelMessage(channelName, dataObject);
  }

  sendRaiseHandMessage(callUuid, channelName = "CallChannel") {
    this.#sendChannelMessage(channelName, { call_uuid: callUuid, update: "raise_hand" });
  }

  sendLowerHandMessage(callUuid, channelName = "CallChannel") {
    this.#sendChannelMessage(channelName, { call_uuid: callUuid, update: "lower_hand" });
  }

  sendSyncRaisedHandsMessage(callUuid, channelName = "CallChannel") {
    this.#sendChannelMessage(channelName, { call_uuid: callUuid, update: "sync_raised_hands" });
  }

  addConnectListener(listener) {
    return this.ws.addConnectListener(listener);
  }

  sendApproveTranslationMessage(callUuid) {
    const dataObject = {
      call_uuid: callUuid,
      update: "approve_translations",
    };
    this.#sendCallMessage(dataObject);
  }



  sendTranslationEnabled(callUuid, enabled, translationType, channelName = "CallChannel") {
    const dataObject = {
      call_uuid: callUuid,
      enabled: enabled,
      translation_type: translationType
    };
    console.log(`[WebSocket] Sending translation message to ${channelName}:`, dataObject);
    this.#sendChannelMessage(channelName, dataObject);
  }

  sendMoodMessage(callUuid, mood) {
    const dataObject = {
      call_uuid: callUuid,
      mood: mood,
    };
    this.#sendCallMessage(dataObject);
  }

  sendVoiceEnabledMessage(userId, voiceEnabled) {
    const dataObject = {
      user_id: userId,
      voice_enabled: voiceEnabled,
    };
    this.#sendCallMessage(dataObject);
  }

  sendTranscribedMessage(callUuid, userId, deviceSentDate, message) {
    const dataObject = {
      call_uuid: callUuid,
      user_id: userId,
      device_sent_date: deviceSentDate,
      message: message,
    };
    this.#sendCallMessage(dataObject);
  }

  sendConferenceTranscribedMessage(callUuid, userId, deviceSentDate, message) {
    const dataObject = {
      call_uuid: callUuid,
      user_id: userId,
      device_sent_date: deviceSentDate,
      message: message,
      message_type: "transcribed_message",
    };
    this.#sendChannelMessage("ConferenceCallChannel", dataObject);
  }

  sendConferenceLanguageUpdate(callUuid, languageKey) {
    if (!callUuid || !languageKey) {
      return;
    }

    const dataObject = {
      message_type: "update_language",
      call_uuid: callUuid,
      language: languageKey,
    };

    console.log("[WS][ConferenceCallChannel] send update_language", {
      callUuid,
      languageKey,
      connected: this.ws?.isConnected?.(),
    });

    this.#sendChannelMessage("ConferenceCallChannel", dataObject);
  }

  updateConferenceChannelSubscription(callUuid, languageKey, joinMode) {
    if (!callUuid || !languageKey) {
      return;
    }
    const params = {
      call_uuid: callUuid,
      language: languageKey,
    };
    if (joinMode) {
      params.join_mode = joinMode;
    }
    this.ws.updateChannelParams("ConferenceCallChannel", params);
  }

  clearConferenceChannelSubscription(callUuid, languageKey, joinMode) {
    if (!callUuid || !languageKey) {
      return;
    }
    const params = {
      call_uuid: callUuid,
      language: languageKey,
    };
    if (joinMode) {
      params.join_mode = joinMode;
    }
    this.ws.unsubscribeFromChannel("ConferenceCallChannel", params);
  }

  sendConferenceSpeakerResync(callUuid, userId) {
    const dataObject = {
      call_uuid: callUuid,
      user_id: userId,
      message_type: "resync_speaker_agent",
    };
    this.#sendChannelMessage("ConferenceCallChannel", dataObject);
  }

  // Private methods

  #sendCallMessage(dataObject) {
    this.ws.sendMessage("CallChannel", dataObject);
  }

  #sendChannelMessage(channelName, dataObject) {
    this.ws.sendMessage(channelName, dataObject);
  }

  #sendMeetingMessage(dataObject) {
    this.ws.sendMessage("MeetingChannel", dataObject);
  }

  sendApproveJoinMeetingMessage(meeting_id, participant_id) {
    const dataObject = {
      meeting_id: meeting_id,
      participant_id: participant_id,
      status: "approved",
    };
    this.#sendMeetingMessage(dataObject);
  }

  sendDenyJoinMeetingMessage(meeting_id, participant_id) {
    const dataObject = {
      meeting_id: meeting_id,
      participant_id: participant_id,
      status: "rejected",
    };
    this.#sendMeetingMessage(dataObject);
  }

  #notify(type, message) {
    this.observers.forEach((observer, index) => {
      if (observer.channelName === type) {
        observer.update(message);
      }
    });
  }

  #subscribeToChannels() {
    const self = this;
    WebSocketHandler.CHANNEL_NAMES.forEach((name, index) => {
      this.ws.subscribeToChannel(name, (type, message) => {
        this.#notify(type, message);
      });
    });
  }
}

export default WebSocketHandler;
